import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { SatelliteLockService } from 'src/app/modules/satellite-lock/satellite-lock.service';
import { SatelliteLock } from 'src/app/core/interfaces/satelliteLock';
import { SatelliteLockType } from 'src/app/core/interfaces/satelliteLockType';
import { SatelliteLockStateDevice } from 'src/app/core/enums/satelliteLockStateDevice.enum';
import { Patterns } from 'src/app/core/resources/patterns';
import { Utils } from 'src/app/core/resources/utils';
import { FileService } from 'src/app/shared/files/file.service';
import { FileNamePipe } from 'src/app/core/pipe/fileName.pipe';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DialogComponent } from 'src/app/shared/dialog/dialog.component';
import { ModalEnum } from 'src/app/core/enums/modal.enum';
import { Permission } from 'src/app/core/resources/permission';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { RoleService } from 'src/app/core/services/role.service';
import { AuthService } from 'src/app/core/services/authentication.service';
import { FormMessages } from 'src/app/core/messages/form-messages.enum';
import { Fmt } from 'src/app/core/messages/fmt';
import { ResourceMessages } from 'src/app/core/messages/resource-messages.enum';
import { Subscription } from 'rxjs';

/**
 * Component for displaying and editing satellite lock details
 */
@Component({
  selector: 'app-detail-satellite-lock',
  templateUrl: './detail-satellite-lock.component.html',
  styleUrls: ['./detail-satellite-lock.component.scss']
})
export class DetailSatelliteLockComponent implements OnInit {
  @ViewChild('inputImage') inputImage: ElementRef;

  permission = Permission;
  permissionRole: PermissionRole;
  satelliteLock: SatelliteLock;
  satelliteLockForm: FormGroup;
  visualImage: FormControl = new FormControl('');
  imageResource: File | { url: string, type: string } | null = null;
  fileResource: File | null = null;
  errorLoadImage: boolean = false;
  loadingImage: boolean = false;



  typeAutocompleteOptions = {
    title: 'Tipo de dispositivo',
    appearance: 'outline',
    initialValue: ''
  };
  typeValidate: string = '';
  readonly SatelliteLockStateDevice = SatelliteLockStateDevice;
  stateDeviceSubscription: Subscription;
  stateDeviceOptions = [
    { value: SatelliteLockStateDevice.ACTIVE, label: 'Activo' },
    { value: SatelliteLockStateDevice.INACTIVE, label: 'Inactivo' },
    { value: SatelliteLockStateDevice.IN_MAINTENANCE, label: 'En mantenimiento' }
  ];

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly satelliteLockService: SatelliteLockService,
    private readonly spinner: NgxSpinnerService,
    private readonly snackBarService: SnackBarService,
    public readonly patterns: Patterns,
    private readonly utils: Utils,
    private readonly fileService: FileService,
    private readonly fileNamePipe: FileNamePipe,
    private readonly dialog: MatDialog,
    permissionRole: PermissionRole
  ) {
    this.permissionRole = permissionRole;
    this.satelliteLockForm = this.formBuilder.group({
      id: ['', [Validators.required, Validators.pattern(this.patterns.SATELLITE_LOCK_ID_FIRMWARE.source)]],
      type: ['', Validators.required],
      imei: ['', [Validators.required]],
      consecutive: ['', [Validators.required]],
      firmwareVersion: ['', [Validators.pattern(this.patterns.SATELLITE_LOCK_ID_FIRMWARE.source)]],
      state: [true],
      documentId: [''],
      stateDevice: [SatelliteLockStateDevice.ACTIVE, Validators.required],
      description: [{ value: '', disabled: true }, []],
      image: ['']
    });
  }

  ngOnInit(): void {
    this.loadSatelliteLock();
  }

  private loadSatelliteLock(): void {
    const imei = this.route.snapshot.params['imei'];
    if (!imei) {
      this.snackBarService.openSnackBar('IMEI de candado satelital no encontrado', undefined, 'error');
      this.goBack();
      return;
    }

    this.spinner.show();
    this.satelliteLockService.getSatelliteLockByImei(imei).subscribe(
      (satelliteLock: SatelliteLock) => {
        this.satelliteLock = satelliteLock;
        this.satelliteLockForm.patchValue(this.satelliteLock);
        this.addValidatorsToForm();
        this.loadImagePreview();
        this.spinner.hide();
        this.updateAutocompleteInitialValue();
      },
      (error) => {
        this.spinner.hide();
        this.snackBarService.openSnackBar('Error al cargar el candado satelital', undefined, 'error');
        this.goBack();
      }
    );
  }

  private addValidatorsToForm(): void {
    const idControl = this.satelliteLockForm.get('id');
    if (idControl) idControl.setValidators([Validators.required, Validators.pattern(this.patterns.SATELLITE_LOCK_ID_FIRMWARE.source)]);
    const typeControl = this.satelliteLockForm.get('type');
    if (typeControl) typeControl.setValidators([Validators.required]);
    const consecutiveControl = this.satelliteLockForm.get('consecutive');
    if (consecutiveControl) consecutiveControl.setValidators([Validators.required]);
    const stateDeviceControl = this.satelliteLockForm.get('stateDevice');
    if (stateDeviceControl) stateDeviceControl.setValidators([Validators.required]);
    const firmwareVersionControl = this.satelliteLockForm.get('firmwareVersion');
    if (firmwareVersionControl) firmwareVersionControl.setValidators([Validators.pattern(this.patterns.SATELLITE_LOCK_ID_FIRMWARE.source)]);
    this.setupStateDeviceListener();
    this.satelliteLockForm.updateValueAndValidity();
  }

  private setupStateDeviceListener(): void {
    const stateDeviceControl = this.satelliteLockForm.get('stateDevice');
    const descriptionControl = this.satelliteLockForm.get('description');
    if (stateDeviceControl && descriptionControl) {
      this.stateDeviceSubscription = stateDeviceControl.valueChanges.subscribe((newState: SatelliteLockStateDevice) => {
        this.updateDescriptionFieldState(newState);
      });
      this.updateDescriptionFieldState(stateDeviceControl.value);
    }
  }

  private updateDescriptionFieldState(stateDevice: SatelliteLockStateDevice): void {
    const descriptionControl = this.satelliteLockForm.get('description');
    if (!descriptionControl) return;
    const isActive = stateDevice === SatelliteLockStateDevice.ACTIVE;

    if (isActive) {
      descriptionControl.disable();
      descriptionControl.clearValidators();
      descriptionControl.setValue('');
    } else {
      descriptionControl.enable();
      descriptionControl.clearValidators();
      descriptionControl.setValidators([Validators.required]);
    }
    descriptionControl.updateValueAndValidity();
  }


  private updateAutocompleteInitialValue(): void {
    if (!this.satelliteLock || !this.satelliteLock.type) return;
    const typeId = typeof this.satelliteLock.type === 'object'
      ? this.satelliteLock.type.id
      : this.satelliteLock.type;

    if (typeId) {
      this.typeAutocompleteOptions = {
        title: 'Tipo de dispositivo',
        appearance: 'outline',
        initialValue: typeId.toString().trim()
      };
    }
  }

  private loadImagePreview(): void {
    this.imageResource = null;
    this.errorLoadImage = false;

    if (this.satelliteLock && this.satelliteLock.image) {
      let imagePath = this.satelliteLock.image.trim();
      if (!imagePath || imagePath === '') {
        this.visualImage.setValue('');
        return;
      }
      try {
        const storage = AuthService.fStorage;
        const pathReference = storage.ref(`${this.satelliteLockService.pathImageSatelliteLock.replace('%s', this.satelliteLock.imei)}/${imagePath}`);

        pathReference.getDownloadURL()
          .then((downloadURL: string) => {
            this.imageResource = {
              url: downloadURL,
              type: imagePath.split('.').pop() || 'jpg'
            };
            this.visualImage.setValue(this.fileNamePipe.transform(imagePath) || 'Imagen cargada');
          })
          .catch((error) => {
            console.error('Error al obtener URL de descarga de la imagen:', error);
            this.errorLoadImage = true;
            this.visualImage.setValue(this.fileNamePipe.transform(imagePath) || 'Imagen no disponible');
          });
      } catch (error) {
        console.error('Error al cargar la imagen:', error);
        this.errorLoadImage = true;
        this.visualImage.setValue(this.fileNamePipe.transform(imagePath) || 'Imagen no disponible');
      }
    } else {
      this.visualImage.setValue('');
    }
  }

  private checkCustomValidations(): boolean {
    this.satelliteLockForm.markAllAsTouched();
    const validations = [
      { control: 'consecutive', label: 'consecutivo' },
      { control: 'type', label: 'tipo de dispositivo' },
      { control: 'id', label: 'ID del dispositivo' },
      { control: 'stateDevice', label: 'estado del dispositivo' },
      { control: 'description', label: 'descripción' },
      { control: 'firmwareVersion', label: 'versión de firmware' },
    ];

    for (const validation of validations) {
      const control = this.satelliteLockForm.get(validation.control);
      if (this.utils.errorMessagesCustomized(control, validation.label)) return true;
    }
    return false;
  }

  async onSubmit(): Promise<void> {
    ['id', 'firmwareVersion', 'description'].forEach(controlName => {
      const control = this.satelliteLockForm.get(controlName);
      if (control) control.setValue(control.value.trim());
    });
    if (this.checkCustomValidations()) return;
    this.spinner.show();
    if (this.fileResource) {
      const pathImage = this.satelliteLockService.pathImageSatelliteLock.replace('%s', this.satelliteLock.imei);
      const uploadImage = await this.uploadImageFile(`${pathImage}/${this.fileResource.name}`, this.fileResource);
      if (!uploadImage) {
        this.spinner.hide();
        return this.snackBarService.openSnackBar(Fmt.string(ResourceMessages.ERROR_UPLOAD, 'la imagen'), undefined, 'error');
      }
      this.satelliteLockForm.get('image').setValue(this.fileResource.name);
    }
    const formValue = this.satelliteLockForm.getRawValue();
    this.satelliteLockService.updateSatelliteLock(formValue as SatelliteLock).subscribe(
      (updated: SatelliteLock) => {
        this.spinner.hide();
        this.fileResource = null;
        this.loadSatelliteLock();
        this.snackBarService.openSnackBar('Candado satelital actualizado exitosamente', undefined, 'success');
      },
      (error) => {
        this.spinner.hide();
        let errorMessage = 'Error al actualizar el candado satelital';
        if (error && error.error && typeof error.error === 'string') errorMessage = error.error;
        this.snackBarService.openSnackBar(
          errorMessage,
          undefined,
          'error'
        );
      }
    );
  }


  onDeleteSatelliteLock(satelliteLock: SatelliteLock): void {
    const dialogConfig = new MatDialogConfig();
    const lockIdentifier = satelliteLock.consecutive || satelliteLock.id || 'el candado satelital';
    const title = `¿Estás seguro que deseas eliminar ${lockIdentifier}?`;

    dialogConfig.data = {
      title: title,
    };
    dialogConfig.width = ModalEnum.EXTRA_SMALL_WIDTH;
    dialogConfig.autoFocus = false;
    dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
    dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;

    const dialogRef = this.dialog.open(DialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.state) {
        this.deleteSatelliteLock(satelliteLock);
      }
    });
  }

  private deleteSatelliteLock(satelliteLock: SatelliteLock): void {
    if (!satelliteLock || !satelliteLock.imei) {
      this.snackBarService.openSnackBar(
        'Error: No se puede eliminar el candado satelital - IMEI no encontrado',
        undefined,
        'error'
      );
      return;
    }

    this.spinner.show();
    this.satelliteLockService.deleteSatelliteLock(satelliteLock.imei).subscribe(
      () => {
        this.spinner.hide();
        this.snackBarService.openSnackBar(
          'Candado satelital eliminado exitosamente',
          undefined,
          'success'
        );
        this.goBack();
      },
      (error) => {
        this.spinner.hide();
        this.snackBarService.openSnackBar(
          error && error.error ? error.error : 'Error al eliminar el candado satelital',
          undefined,
          'error'
        );
      }
    );
  }




  getStateDeviceText(): string {
    if (!this.satelliteLock || !this.satelliteLock.stateDevice) return '-';
    const stateMap = {
      [SatelliteLockStateDevice.ACTIVE]: 'Activo',
      [SatelliteLockStateDevice.INACTIVE]: 'Inactivo',
      [SatelliteLockStateDevice.IN_MAINTENANCE]: 'En mantenimiento'
    };

    return stateMap[this.satelliteLock.stateDevice] || this.satelliteLock.stateDevice.toString();
  }

  goBack(): void {
    this.router.navigate(['/administration/satellite-lock/list']);
  }


  getErrorMessage(controlName: string): string {
    const control = this.satelliteLockForm.get(controlName);

    if (control && control.errors && control.touched) {
      if (control.errors['required']) return 'Este campo es requerido';
      if (control.errors['minlength']) return `Mínimo ${control.errors['minlength'].requiredLength} caracteres`;
      if (control.errors['maxlength']) return `Máximo ${control.errors['maxlength'].requiredLength} caracteres`;
      if (control.errors['pattern']) {
        if (controlName === 'id' || controlName === 'firmwareVersion') return 'Solo se permiten letras, números y los caracteres . - _ / #';
        return 'Formato inválido';
      }
    }

    return '';
  }


  hasError(controlName: string): boolean {
    const control = this.satelliteLockForm.get(controlName);
    return !!(control && control.errors && control.touched);
  }

  setInstanceFileStorage(e: Event): void {
    if (!e.target || !e.target['files'] || !e.target['files'].length) return;
    const file = e.target['files'][0];
    if (!this.utils.isImage(file)) return this.snackBarService.openSnackBar('El archivo no es una imagen', undefined, 'error');
    const splittedNameFile = file.name.split('.');
    const formatFile = splittedNameFile[splittedNameFile.length - 1];
    const dateInMs = new Date().getTime();
    const fileName = `satelliteLockImage_${dateInMs}.${formatFile}`;
    this.fileResource = new File([file], fileName, {
      type: file.type
    });

    this.visualImage.setValue(this.fileNamePipe.transform(fileName) || fileName);
    this.imageResource = file;
    this.errorLoadImage = false;
  }


  private async uploadImageFile(filePath: string, imageFile: File): Promise<boolean> {
    try {
      const uploadResult = await this.fileService.uploadFile({
        path: filePath,
        imageFile: imageFile,
        key: 'image'
      });
      return uploadResult.status;
    } catch (error) {
      return false;
    }
  }

  openWindowFile(): void {
    if (this.inputImage && this.inputImage.nativeElement) this.inputImage.nativeElement.click();
  }

  get isReadOnlyMode(): boolean {
    return !this.permissionRole.hasPermission(
      Permission.administration.module,
      Permission.administration.updateSatelliteLock
    );
  }

  get isDescriptionEnabled(): boolean {
    const descriptionControl = this.satelliteLockForm.get('description');
    return descriptionControl ? !descriptionControl.disabled : false;
  }

  ngOnDestroy(): void {
    if (this.stateDeviceSubscription) this.stateDeviceSubscription.unsubscribe();
  }
}
